import { Panel, Container, Button, SliderInput, Label, TextInput } from '@playcanvas/pcui/react';
import React from 'react';

import { SetProperty, ObserverData, IndividualAnimationData } from '../../types';

interface AnimationItemProps {
    animation: IndividualAnimationData;
    setProperty: SetProperty;
}

const AnimationItem: React.FC<AnimationItemProps> = ({ animation, setProperty }) => {
    const currentTime = animation.progress * animation.duration;
    const itemClass = animation.isIndividualChannel ?
        ['animation-item', 'animation-channel-item'] :
        ['animation-item'];

    return (
        <Container class={itemClass} flexDirection='column' flex>
            <Container class='animation-header' flexDirection='row' flex>
                <Button
                    class='animation-play-button'
                    width={28}
                    height={24}
                    icon={animation.playing ? 'E376' : 'E286'}
                    text=''
                    onClick={() => {
                        setProperty(`animations.${animation.name}.playing`, !animation.playing);
                    }}
                />
                <Label
                    class='animation-name'
                    text={animation.displayName || animation.name}
                />
                <Label
                    class='animation-time'
                    text={`${currentTime.toFixed(2)} / ${animation.duration.toFixed(2)}s`}
                />
            </Container>
            <SliderInput
                class='animation-slider'
                min={0}
                max={1}
                sliderMin={0}
                sliderMax={1}
                precision={3}
                step={0.001}
                value={animation.progress}
                onChange={(value: number) => {
                    setProperty(`animations.${animation.name}.progress`, value);
                }}
            />
        </Container>
    );
};

interface AnimationPanelProps {
    animationData: ObserverData['animation'];
    animations: Record<string, IndividualAnimationData>;
    setProperty: SetProperty;
}

interface AnimationPanelState {
    searchQuery: string;
    collapsed: boolean;
    viewMode: 'clips' | 'channels';
}

class AnimationPanel extends React.Component<AnimationPanelProps, AnimationPanelState> {
    private panelRef = React.createRef<any>();

    private panelEvents: Array<{ unbind: () => void }> = [];

    private setupTimeout: ReturnType<typeof setTimeout> | null = null;

    state: AnimationPanelState = {
        searchQuery: '',
        collapsed: true,  // Start collapsed (closed) by default
        viewMode: 'clips'
    };

    componentDidMount(): void {
        this.setupTimeout = setTimeout(() => {
            const pcuiPanel = this.getPcuiPanel();
            if (!pcuiPanel || typeof pcuiPanel.on !== 'function') return;

            this.panelEvents = [
                pcuiPanel.on('collapse', () => this.setCollapsed(true)),
                pcuiPanel.on('expand', () => this.setCollapsed(false))
            ];

            pcuiPanel.collapsed = this.state.collapsed;
        }, 0);
    }

    componentWillUnmount(): void {
        if (this.setupTimeout) {
            clearTimeout(this.setupTimeout);
        }

        this.panelEvents.forEach(event => event.unbind());
        this.panelEvents = [];
    }

    getPcuiPanel = () => {
        const panel = this.panelRef.current;
        return panel?.element || panel?.pcuiPanel || panel || null;
    };

    setPcuiPanelCollapsed = (collapsed: boolean) => {
        const pcuiPanel = this.getPcuiPanel();
        if (pcuiPanel && typeof pcuiPanel.collapsed !== 'undefined' && pcuiPanel.collapsed !== collapsed) {
            pcuiPanel.collapsed = collapsed;
        }
    };

    setCollapsed = (collapsed: boolean) => {
        if (this.state.collapsed !== collapsed) {
            this.setState({ collapsed });
        }
    };

    updateSearchQuery = (searchQuery: string) => {
        this.setPcuiPanelCollapsed(false);
        this.setState({ searchQuery, collapsed: false });
    };

    clearSearch = () => {
        this.setPcuiPanelCollapsed(false);
        this.setState({ searchQuery: '', collapsed: false });
    };

    setViewMode = (viewMode: AnimationPanelState['viewMode']) => {
        this.setPcuiPanelCollapsed(false);
        this.setState({ viewMode, collapsed: false });
    };

    setAnimationProperty = (path: string, value: unknown) => {
        this.setPcuiPanelCollapsed(false);
        if (this.state.collapsed) {
            this.setState({ collapsed: false }, () => this.props.setProperty(path, value));
        } else {
            this.props.setProperty(path, value);
        }
    };

    shouldComponentUpdate(nextProps: AnimationPanelProps, nextState: Readonly<AnimationPanelState>): boolean {
        return (
            JSON.stringify(nextProps.animations) !== JSON.stringify(this.props.animations) ||
            nextProps.animationData.speed !== this.props.animationData.speed ||
            nextProps.animationData.list !== this.props.animationData.list ||
            nextState.searchQuery !== this.state.searchQuery ||
            nextState.collapsed !== this.state.collapsed ||
            nextState.viewMode !== this.state.viewMode
        );
    }

    render() {
        const { animations, animationData } = this.props;
        const { searchQuery, viewMode } = this.state;
        const animationsList: Array<string> = JSON.parse(animationData.list).filter((name: string) => animations[name]);
        const clipNames = animationsList.filter(name => !animations[name].isIndividualChannel);
        const channelNames = animationsList.filter(name => animations[name].isIndividualChannel);
        const hasAnimations = clipNames.length > 0;

        if (!hasAnimations) {
            return null;
        }

        const visibleAnimationNames = viewMode === 'clips' ? clipNames : channelNames;
        const normalizedQuery = searchQuery.trim().toLowerCase();
        const filteredAnimations = normalizedQuery ?
            visibleAnimationNames.filter((name) => {
                const animation = animations[name];
                const searchableText = `${name} ${animation.displayName || ''} ${animation.sourceAnimation || ''}`;
                return searchableText.toLowerCase().includes(normalizedQuery);
            }) :
            visibleAnimationNames;
        const hasMatches = filteredAnimations.length > 0;
        const channelGroups = filteredAnimations.reduce<Record<string, string[]>>((groups, name) => {
            const sourceAnimation = animations[name].sourceAnimation || 'Other';
            (groups[sourceAnimation] ||= []).push(name);
            return groups;
        }, {});

        return (
            <Panel ref={this.panelRef} headerText='ANIMATIONS' id='animation-panel' collapsible={true} flexShrink={'0'} flexGrow={'0'} collapsed={this.state.collapsed}>
                <Container class='animation-view-toggle' flexDirection='row'>
                    <Button
                        class={viewMode === 'clips' ? ['animation-view-button', 'active'] : ['animation-view-button']}
                        text={`Clips (${clipNames.length})`}
                        onClick={() => this.setViewMode('clips')}
                    />
                    <Button
                        class={viewMode === 'channels' ? ['animation-view-button', 'active'] : ['animation-view-button']}
                        text={`Channels (${channelNames.length})`}
                        onClick={() => this.setViewMode('channels')}
                    />
                </Container>

                <Container class='animation-search' flexDirection='row'>
                    <TextInput
                        class='animation-search-input'
                        placeholder={`Search ${viewMode}`}
                        keyChange
                        value={searchQuery}
                        onChange={this.updateSearchQuery}
                    />
                    <Button
                        class='animation-search-clear'
                        text='Clear'
                        enabled={normalizedQuery.length > 0}
                        onClick={this.clearSearch}
                    />
                </Container>

                <Container class='animation-list' flexDirection='column'>
                    {hasMatches ? (
                        viewMode === 'clips' ? (
                            filteredAnimations.map(name => (
                                <AnimationItem
                                    key={name}
                                    animation={animations[name]}
                                    setProperty={this.setAnimationProperty}
                                />
                            ))
                        ) : (
                            Object.entries(channelGroups).map(([sourceAnimation, names]) => (
                                <Container key={sourceAnimation} class='animation-channel-group' flexDirection='column'>
                                    <Label class='animation-channel-group-name' text={sourceAnimation} />
                                    {names.map(name => (
                                        <AnimationItem
                                            key={name}
                                            animation={animations[name]}
                                            setProperty={this.setAnimationProperty}
                                        />
                                    ))}
                                </Container>
                            ))
                        )
                    ) : (
                        <Container class='animation-empty' flexDirection='column'>
                            <Label text={`No ${viewMode} found`} />
                            <Button text='Clear search' onClick={this.clearSearch} />
                        </Container>
                    )}
                </Container>
            </Panel>
        );
    }
}

export default AnimationPanel;
